import { Card } from "@/ingot";
import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import {
  useDismissQuoteRepair,
  useRestoreQuoteRepair,
  useToggleQuoteRepair,
  type RepairResponse,
} from "@/api/queries/quotes";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { RepairBeforeAfter } from "@/components/dxf/RepairBeforeAfter";

import { Pill } from "@/components/ui/Pill";

interface Props {
  quoteSlug: string;
  fileName: string;
  originalPreviewUrl: string;
  repair: RepairResponse;
}

/**
 * Side-by-side compare of the original DXF vs. the auto-repaired
 * "manufacturing copy" — mockup 06 Před / po auto-opravě.
 *
 * Two `DxfPreview` instances render the original (left) and the
 * repaired SVG (right); a pill list under the comparison enumerates
 * each applied fix; the footer shows the aggregate metric delta
 * (hole area, total length, open count) and a Czech tagline noting
 * that the original DXF is untouched.
 *
 * Pricing is unaffected by what's shown here — the toggle that flips
 * authoritative geometry to the repaired copy lands in Phase 5e.
 */
export function RepairCompare({
  quoteSlug,
  fileName,
  originalPreviewUrl,
  repair,
}: Props): JSX.Element | null {
  const { t } = useTranslation("quote");
  const toggle = useToggleQuoteRepair(quoteSlug);
  const dismiss = useDismissQuoteRepair(quoteSlug);
  const restore = useRestoreQuoteRepair(quoteSlug);
  const [confirmOpen, setConfirmOpen] = useState(false);
  if (repair.changes.length === 0) return null;
  const usesRepaired = repair.uses_repaired_for_pricing;

  if (repair.dismissed) {
    return (
      <Card padded data-testid="repair-compare-dismissed">
        <div className="flex items-center justify-between gap-3 text-[12px] text-ink-3">
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden="true">ⓘ</span>
            {t("repair.dismiss.dismissedLabel")}
          </span>
          <button
            type="button"
            onClick={() => restore.mutate()}
            disabled={restore.isPending}
            data-testid="repair-restore"
            className="text-ink-2 underline hover:text-ink disabled:opacity-50"
          >
            {t("repair.dismiss.restoreLink")}
          </button>
        </div>
      </Card>
    );
  }

  return (
    <Card padded={false} data-testid="repair-compare">
      <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-ink">
            {t("repair.title")}
          </h3>
          <Pill tone="ok">
            {t("repair.changeCount", { count: repair.changes.length })}
          </Pill>
        </div>
        <span className="mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
          {t("repair.manufacturingCopy")}
        </span>
      </header>

      <RepairBeforeAfter
        fileName={fileName}
        originalPreviewUrl={originalPreviewUrl}
        repairedPreviewUrl={repair.repaired_preview_url}
      />

      <footer className="flex flex-col gap-2 border-t border-border px-4 py-3 text-[11px] text-ink-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
          <span>{t("repair.originalUntouched")}</span>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => toggle.mutate(!usesRepaired)}
              disabled={toggle.isPending}
              data-testid="repair-toggle-pricing"
              className={
                usesRepaired
                  ? "inline-flex h-7 items-center gap-1.5 rounded-md border border-ok-border bg-ok-bg px-2 text-[11px] font-medium text-ok hover:opacity-90 disabled:opacity-50"
                  : "inline-flex h-7 items-center gap-1.5 rounded-md border border-border-strong bg-surface px-2 text-[11px] text-ink hover:bg-surface-2 disabled:opacity-50"
              }
            >
              {usesRepaired
                ? t("repair.toggle.activeLabel")
                : t("repair.toggle.inactiveLabel")}
            </button>
            {!usesRepaired && (
              <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                disabled={dismiss.isPending}
                data-testid="repair-dismiss"
                className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border-strong bg-surface px-2 text-[11px] text-ink-2 hover:bg-surface-2 disabled:opacity-50"
              >
                {t("repair.dismiss.button")}
              </button>
            )}
          </div>
        </div>
        <span className="mono tabular-nums" data-testid="repair-summary-delta">
          {formatSummaryDelta(repair.summary_metric_delta, t)}
        </span>
      </footer>
      <ConfirmDialog
        open={confirmOpen}
        title={t("repair.dismiss.modalTitle")}
        description={t("repair.dismiss.modalBody")}
        confirmLabel={t("repair.dismiss.confirm")}
        cancelLabel={t("repair.dismiss.cancel")}
        busy={dismiss.isPending}
        onConfirm={() => {
          dismiss.mutate(undefined, {
            onSuccess: () => setConfirmOpen(false),
          });
        }}
        onClose={() => setConfirmOpen(false)}
        testId="repair-dismiss-confirm"
      />
    </Card>
  );
}

export function formatSummaryDelta(
  delta: Record<string, number>,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const parts: string[] = [];
  const areaDelta = delta?.hole_area_mm2_delta;
  const lenDelta = delta?.total_length_mm_delta;
  const openDelta = delta?.open_count_delta;
  if (typeof areaDelta === "number" && Math.abs(areaDelta) >= 0.5) {
    parts.push(
      t("repair.summary.areaDelta", {
        sign: areaDelta >= 0 ? "+" : "−",
        value: (Math.abs(areaDelta) / 1_000_000).toFixed(3),
      }),
    );
  }
  if (typeof lenDelta === "number" && Math.abs(lenDelta) >= 0.5) {
    parts.push(
      t("repair.summary.lengthDelta", {
        sign: lenDelta >= 0 ? "+" : "−",
        value: Math.abs(lenDelta).toFixed(1),
      }),
    );
  }
  if (typeof openDelta === "number" && openDelta > 0) {
    parts.push(t("repair.summary.openClosed", { count: openDelta }));
  }
  return parts.join(" · ");
}
